import {BranchMove} from './moveTreeBranchOps';

/**
 * Path-aware navigation over the chapter move tree, shared by the move
 * list's keyboard bindings and the study screen's nav buttons / autoplay so
 * every entry point walks lines the same way lichess does:
 *
 *  - "next" follows the line you are on (a variation's own continuation,
 *    never a DFS jump into a sibling branch),
 *  - "prev" steps to the move actually played before this one,
 *  - "line end" runs the current line out to its tip.
 *
 * Tree shape (see moveTreeOps.ts): the top-level array IS the mainline;
 * a mainline node's child_moves are all alternatives to its successor;
 * inside a variation, child_moves[0] is that line's continuation and
 * child_moves[1..] are sub-variations.
 */

export interface NavEntry {
  move: BranchMove;
  /** The move played immediately before this one (null at move 1). */
  prev: BranchMove | null;
  /** The move that continues this one's line (null at the line's tip). */
  next: BranchMove | null;
  /** Alternative continuations branching off after this move. */
  alternatives: BranchMove[];
  /** True for top-level (mainline) nodes. */
  isMainline: boolean;
}

export type NavIndex = Map<string, NavEntry>;

const keyOf = (id: string | number | null | undefined) =>
  id == null ? '' : String(id);

/**
 * One walk of the tree producing prev/next/alternatives per node. The walk
 * is what defines line-following semantics; every helper below is a lookup.
 */
export function buildNavIndex(tree: BranchMove[] | undefined | null): NavIndex {
  const index: NavIndex = new Map();
  if (!tree?.length) {
    return index;
  }

  const walkVariation = (node: BranchMove, prev: BranchMove | null) => {
    if (!node) {
      return;
    }
    const children = node.child_moves || [];
    const continuation = children[0] || null;
    const alternatives = children.slice(1);
    index.set(keyOf(node.id), {
      move: node,
      prev,
      next: continuation,
      alternatives,
      isMainline: false,
    });
    if (continuation) {
      walkVariation(continuation, node);
    }
    alternatives.forEach(alt => walkVariation(alt, node));
  };

  tree.forEach((node, i) => {
    if (!node) {
      return;
    }
    const alternatives = node.child_moves || [];
    index.set(keyOf(node.id), {
      move: node,
      prev: i > 0 ? tree[i - 1] : null,
      next: i + 1 < tree.length ? tree[i + 1] : null,
      alternatives,
      isMainline: true,
    });
    alternatives.forEach(alt => walkVariation(alt, node));
  });

  return index;
}

const isStart = (id: string | number | null | undefined) =>
  id == null || id === 'start' || id === '';

/**
 * The move after `fromId` on its own line. From the start position this is
 * move 1; null when the line has run out (autoplay uses that to stop).
 */
export function getNextMove(
  tree: BranchMove[] | undefined | null,
  index: NavIndex,
  fromId: string | number | null | undefined,
): BranchMove | null {
  if (isStart(fromId)) {
    return tree?.[0] || null;
  }
  return index.get(keyOf(fromId))?.next || null;
}

/**
 * The move played before `fromId`. Returns 'start' when stepping back off
 * move 1, so callers can reset to the chapter's initial position.
 */
export function getPrevMove(
  index: NavIndex,
  fromId: string | number | null | undefined,
): BranchMove | 'start' | null {
  if (isStart(fromId)) {
    return null;
  }
  const entry = index.get(keyOf(fromId));
  if (!entry) {
    return null;
  }
  return entry.prev || 'start';
}

/** Follows the current line forward to its tip (mainline tip from start). */
export function getLineEnd(
  tree: BranchMove[] | undefined | null,
  index: NavIndex,
  fromId: string | number | null | undefined,
): BranchMove | null {
  let cursor = isStart(fromId)
    ? tree?.[0] || null
    : index.get(keyOf(fromId))?.move || null;
  if (!cursor) {
    return null;
  }
  let guard = 0;
  while (guard++ < 10000) {
    const next = index.get(keyOf(cursor.id))?.next;
    if (!next) {
      return cursor;
    }
    cursor = next;
  }
  return cursor;
}

/**
 * Shift+→: enter the first variation branching off after `fromId` (from the
 * start position: the first alternative to move 1 — there are none by
 * construction, so this returns null there).
 */
export function getFirstVariation(
  index: NavIndex,
  fromId: string | number | null | undefined,
): BranchMove | null {
  if (isStart(fromId)) {
    return null;
  }
  return index.get(keyOf(fromId))?.alternatives?.[0] || null;
}

/**
 * Shift+←: climb out of the current variation back to the mainline move at
 * its fork. Null when already on the mainline (nothing to exit).
 */
export function getMainlineForkAncestor(
  index: NavIndex,
  fromId: string | number | null | undefined,
): BranchMove | null {
  let entry = index.get(keyOf(fromId));
  if (!entry || entry.isMainline) {
    return null;
  }
  let guard = 0;
  while (entry && !entry.isMainline && guard++ < 10000) {
    const prev = entry.prev;
    if (!prev) {
      return null;
    }
    const prevEntry = index.get(keyOf(prev.id));
    if (!prevEntry) {
      return null;
    }
    entry = prevEntry;
  }
  return entry?.isMainline ? entry.move : null;
}
